/**
 * 서버별 인증 — 서버마다 토큰 출처·헤더 형식·401의 의미가 다르다.
 * - bearer: `Authorization: Bearer <token>` (인증 서버 토큰을 쓰는 코어 API)
 * - raw: `Authorization: <token>` 접두어 없음 (mgrv-api-java 자체 토큰)
 * - header: 고정 키 헤더 (mesher 계열 공유 API 키) — 401은 "키 오류"라 로그아웃 사유가 아니다
 * - none: 인증 없음 (인증 서버의 토큰 발급 요청 등 — 여기에 토큰 갱신을 걸면 무한 반복된다)
 */
export interface TokenSource {
  getToken: () => string | null | Promise<string | null>;
  /** 비밀이 아닌 세션 세대. 계정 변경 뒤 이전 요청·401 처리가 새 세션에 영향을 주지 않게 한다. */
  getGeneration?: () => number;
  /** 읽기 복구 경계를 식별한다. 같은 경계에서 refresh는 성공한 읽기 전 한 번만 허용한다. */
  onReadUnauthorized?: (key: string) => boolean;
  /** 401일 때 한 번 갱신을 시도한다 — 성공하면 true. 없으면 갱신하지 않는다. */
  refresh?: () => Promise<boolean>;
  /** 갱신이 없거나 실패한 401 — 로그아웃·로그인 화면 이동 등 */
  onUnauthorized?: () => void;
  /** 성공한 읽기가 확인되면 이전 401 복구 시도를 끝낸다. */
  onReadSuccess?: (key: string) => void;
}

export type AuthConfig =
  | { scheme: 'none' }
  | { scheme: 'bearer' | 'raw'; tokens: TokenSource; required?: boolean }
  | { scheme: 'header'; name: string; value: string | undefined };

/** 로그인 연동 전 자리표시 — 토큰 없이 요청한다. 인증 방식이 정해지면 구현으로 바꾼다. */
export const noTokens: TokenSource = { getToken: () => null };

/**
 * 동시에 여러 요청이 401을 받아도 갱신은 한 번만 한다(single-flight).
 * 인증 서버는 refresh 토큰을 재사용하면 세션 전체를 폐기하므로 중복 갱신이 곧 로그아웃이 된다.
 */
export function singleFlight(task: () => Promise<boolean>) {
  let inFlight: Promise<boolean> | null = null;
  return () => {
    inFlight ??= task().finally(() => {
      inFlight = null;
    });
    return inFlight;
  };
}

export async function authHeader(auth: AuthConfig): Promise<[string, string] | null> {
  if (auth.scheme === 'none') {
    return null;
  }
  if (auth.scheme === 'header') {
    // 키가 비어 있으면 헤더 자체를 붙이지 않는다(빈 헤더가 서버 쪽 판정을 헷갈리게 한다).
    return auth.value ? [auth.name, auth.value] : null;
  }
  const token = await auth.tokens.getToken();
  if (!token) {
    return null;
  }
  return ['Authorization', auth.scheme === 'bearer' ? `Bearer ${token}` : token];
}
